import { MUI, ThemeMode } from 'ci-esi-web-shared';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { colors } from 'styles/theme/colors';
import { ProductUpdateType } from 'types/ProductUpdate';

import { actions as notificationsActions } from './slice';
import { DATA_UPDATES_VALUE_ATTRIBUTE, NotificationLinkType, ParsedProductUpdate, ProductUpdateState } from './types';

export interface NotificationsListProps {
  notifications?: ParsedProductUpdate[];
  handleSelectNotificationsCategory: (category: ProductUpdateType) => void;
}

export const NotificationsList = ({
  notifications = [],
  handleSelectNotificationsCategory,
}: NotificationsListProps) => {
  const theme = MUI.useTheme();
  const isDarkMode = theme.palette.mode === ThemeMode.DARK;
  const dispatch = useDispatch();
  const descriptionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!descriptionRef.current) return;

    const handleLinkClick = (event: MouseEvent) => {
      const link = (event.target as HTMLElement).closest(`[${DATA_UPDATES_VALUE_ATTRIBUTE}]`);
      if (!link) return;

      switch (link.textContent) {
        case NotificationLinkType.DataUpdates: {
          handleSelectNotificationsCategory(ProductUpdateType.DataUpdate);
          break;
        }
        case NotificationLinkType.PlatformUpdates: {
          handleSelectNotificationsCategory(ProductUpdateType.ProductUpdate);
          break;
        }
        default: {
          return;
        }
      }

      const attributeData = link.getAttribute(DATA_UPDATES_VALUE_ATTRIBUTE);
      const productUpdates: ProductUpdateState = attributeData ? JSON.parse(attributeData) : {};
      dispatch(notificationsActions.setScrollProductUpdateState(productUpdates));
    };

    descriptionRef.current.addEventListener('click', handleLinkClick);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => descriptionRef.current?.removeEventListener('click', handleLinkClick);
  }, [descriptionRef, handleSelectNotificationsCategory, dispatch]);

  const getNotification = useCallback(
    (
      {
        messageId,
        isRead,
        releaseDate,
        description,
      }: { messageId: string; isRead: boolean; releaseDate: ReactNode; description: string },
      index: number,
    ) => {
      const updatesValue = JSON.stringify({ messageId, date: releaseDate } as ProductUpdateState);
      const updatedDescription = description
        .replace(
          NotificationLinkType.DataUpdates,
          `<a data-updates-value='${updatesValue}'>${NotificationLinkType.DataUpdates}</a>`,
        )
        .replace(
          NotificationLinkType.PlatformUpdates,
          `<a data-updates-value='${updatesValue}'>${NotificationLinkType.PlatformUpdates}</a>`,
        );

      return (
        <MUI.Box
          key={`${messageId}${index}`}
          sx={{
            fontSize: 12,
            lineHeight: '16px',
            color: isDarkMode ? colors.primaryWhite : colors.grey56,
            fontWeight: isRead ? undefined : 'bold',
            '&:not(:last-child)': { marginBottom: '26px' },
          }}
          className={String(releaseDate)}
        >
          <MUI.Box sx={{ marginBottom: '6px' }}>
            {!isRead && (
              <MUI.Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 8,
                  height: 8,
                  backgroundColor: colors.stateWarningDefault,
                  borderRadius: '50%',
                  marginRight: '11px',
                }}
              />
            )}
            {releaseDate}
          </MUI.Box>
          <MUI.Box
            sx={{
              '& img': {
                display: 'block',
                margin: '0 auto',
                height: 'auto',
                width: 'auto',
                maxWidth: '100%',
                maxHeight: '500px',
              },
              '& [data-updates-value]': {
                display: 'inline',
                cursor: 'pointer',
                textDecoration: 'underline',
              },
              '& a': { color: colors.baseBlue55 },
            }}
            dangerouslySetInnerHTML={{ __html: updatedDescription }}
          />
        </MUI.Box>
      );
    },
    [isDarkMode],
  );

  const renderNotifications = useMemo(
    () => <div ref={descriptionRef}>{notifications?.map(getNotification)}</div>,
    [notifications, getNotification],
  );

  return renderNotifications;
};
